<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">
              {{ $t('government.unfccc_reporting.title') }}
            </h2>
            <p class="text-gray-600">
              {{ $t('government.unfccc_reporting.subtitle') }}
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- UNFCCC Reporting Content -->
      <main id="unfccc-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Report Generation Section -->
        <div
          id="report-generation"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-medium text-dark-navy">
              {{ $t('government.unfccc_reporting.report_generation.title') }}
            </h3>
            <button
              class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <i class="fa-solid fa-download mr-2"></i>
              {{ $t('government.unfccc_reporting.report_generation.export_all_reports') }}
            </button>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Report Filters -->
            <div id="report-filters" class="space-y-4">
              <h4 class="font-medium text-dark-navy">{{ $t('government.unfccc_reporting.report_generation.report_parameters') }}</h4>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('government.unfccc_reporting.report_generation.reporting_period_label') }}</label
                  >
                  <select
                    class="w-full px-3 py-2 border border-light-gray rounded-md"
                  >
                    <option>2024 Q1</option>
                    <option>2024 Q2</option>
                    <option>2024 Q3</option>
                    <option>2024 Q4</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('government.unfccc_reporting.report_generation.report_type_label') }}</label
                  >
                  <select
                    class="w-full px-3 py-2 border border-light-gray rounded-md"
                  >
                    <option>{{ $t('government.unfccc_reporting.report_generation.report_types.full_report') }}</option>
                    <option>{{ $t('government.unfccc_reporting.report_generation.report_types.summary_report') }}</option>
                    <option>{{ $t('government.unfccc_reporting.report_generation.report_types.adjustment_report') }}</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('government.unfccc_reporting.report_generation.project_categories_label') }}</label
                >
                <div class="space-y-2">
                  <label class="flex items-center">
                    <input
                      type="checkbox"
                      checked
                      class="mr-2 text-forest-green"
                    />
                    <span class="text-sm">{{ $t('government.unfccc_reporting.report_generation.categories.all_categories') }}</span>
                  </label>
                  <label class="flex items-center">
                    <input type="checkbox" class="mr-2 text-forest-green" />
                    <span class="text-sm">{{ $t('government.unfccc_reporting.report_generation.categories.renewable_energy') }}</span>
                  </label>
                  <label class="flex items-center">
                    <input type="checkbox" class="mr-2 text-forest-green" />
                    <span class="text-sm">{{ $t('government.unfccc_reporting.report_generation.categories.forestry_land_use') }}</span>
                  </label>
                  <label class="flex items-center">
                    <input type="checkbox" class="mr-2 text-forest-green" />
                    <span class="text-sm">{{ $t('government.unfccc_reporting.report_generation.categories.agriculture') }}</span>
                  </label>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('government.unfccc_reporting.report_generation.article6_status_label') }}</label
                >
                <div class="space-y-2">
                  <label class="flex items-center">
                    <input
                      type="checkbox"
                      checked
                      class="mr-2 text-forest-green"
                    />
                    <span class="text-sm">{{ $t('government.unfccc_reporting.report_generation.article6_options.article6_eligible') }}</span>
                  </label>
                  <label class="flex items-center">
                    <input type="checkbox" class="mr-2 text-forest-green" />
                    <span class="text-sm">{{ $t('government.unfccc_reporting.report_generation.article6_options.in_buffer') }}</span>
                  </label>
                  <label class="flex items-center">
                    <input type="checkbox" class="mr-2 text-forest-green" />
                    <span class="text-sm">{{ $t('government.unfccc_reporting.report_generation.article6_options.pending_transfer') }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Report Preview -->
            <div id="report-preview" class="space-y-4">
              <h4 class="font-medium text-dark-navy">{{ $t('government.unfccc_reporting.report_preview.title') }}</h4>

              <div class="bg-gray-50 p-4 rounded-md border border-light-gray">
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm font-medium">{{ $t('government.unfccc_reporting.report_preview.estimated_report_size') }}</span>
                  <span class="text-sm text-forest-green">2.4 MB</span>
                </div>
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm font-medium">{{ $t('government.unfccc_reporting.report_preview.total_projects') }}</span>
                  <span class="text-sm">89</span>
                </div>
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm font-medium">{{ $t('government.unfccc_reporting.report_preview.total_credits') }}</span>
                  <span class="text-sm">2,450,000 tCO2e</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium">{{ $t('government.unfccc_reporting.report_preview.last_updated') }}</span>
                  <span class="text-sm text-gray-600">Dec 15, 2024</span>
                </div>
              </div>

              <div class="space-y-2">
                <button
                  class="w-full px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <i class="fa-solid fa-file-pdf mr-2"></i>
                  {{ $t('government.unfccc_reporting.report_preview.generate_pdf_report') }}
                </button>
                <button
                  class="w-full px-4 py-2 bg-electric-blue text-dark-navy rounded-md hover:bg-cyan-400 transition-colors"
                >
                  <i class="fa-solid fa-file-excel mr-2"></i>
                  {{ $t('government.unfccc_reporting.report_preview.generate_excel_report') }}
                </button>
                <button
                  class="w-full px-4 py-2 border border-light-gray text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <i class="fa-solid fa-file-csv mr-2"></i>
                  {{ $t('government.unfccc_reporting.report_preview.generate_csv_report') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Corresponding Adjustments Summary -->
        <div
          id="adjustments-summary"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-medium text-dark-navy">
              {{ $t('government.unfccc_reporting.corresponding_adjustments.title') }}
            </h3>
            <button
              class="px-4 py-2 bg-electric-blue text-dark-navy rounded-md hover:bg-cyan-400 transition-colors"
            >
              <i class="fa-solid fa-sync mr-2"></i>
              {{ $t('government.unfccc_reporting.corresponding_adjustments.refresh_data') }}
            </button>
          </div>

          <!-- Summary Cards -->
          <div
            id="adjustment-cards"
            class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
          >
            <div class="bg-blue-50 p-4 rounded-md border border-light-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-600">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.total_adjustments') }}</p>
                  <p class="text-xl font-medium text-dark-navy">1,890,000</p>
                  <p class="text-xs text-gray-500">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.tco2e') }}</p>
                </div>
                <i
                  class="fa-solid fa-balance-scale text-forest-green text-lg"
                ></i>
              </div>
            </div>

            <div class="bg-green-50 p-4 rounded-md border border-light-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-600">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.positive_adjustments') }}</p>
                  <p class="text-xl font-medium text-forest-green">1,245,000</p>
                  <p class="text-xs text-gray-500">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.tco2e') }}</p>
                </div>
                <i class="fa-solid fa-arrow-up text-forest-green text-lg"></i>
              </div>
            </div>

            <div class="bg-red-50 p-4 rounded-md border border-light-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-600">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.negative_adjustments') }}</p>
                  <p class="text-xl font-medium text-error-red">645,000</p>
                  <p class="text-xs text-gray-500">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.tco2e') }}</p>
                </div>
                <i class="fa-solid fa-arrow-down text-error-red text-lg"></i>
              </div>
            </div>

            <div class="bg-yellow-50 p-4 rounded-md border border-light-gray">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-600">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.pending_adjustments') }}</p>
                  <p class="text-xl font-medium text-yellow-600">156,000</p>
                  <p class="text-xs text-gray-500">{{ $t('government.unfccc_reporting.corresponding_adjustments.stats.tco2e') }}</p>
                </div>
                <i class="fa-solid fa-clock text-yellow-600 text-lg"></i>
              </div>
            </div>
          </div>

          <!-- Adjustments Table -->
          <div id="adjustments-table" class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                  >
                    {{ $t('government.unfccc_reporting.corresponding_adjustments.table.columns.project_id') }}
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                  >
                    {{ $t('government.unfccc_reporting.corresponding_adjustments.table.columns.project_name') }}
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                  >
                    {{ $t('government.unfccc_reporting.corresponding_adjustments.table.columns.sector') }}
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                  >
                    {{ $t('government.unfccc_reporting.corresponding_adjustments.table.columns.adjustment_type') }}
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                  >
                    {{ $t('government.unfccc_reporting.corresponding_adjustments.table.columns.amount_tco2e') }}
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                  >
                    {{ $t('government.unfccc_reporting.corresponding_adjustments.table.columns.status') }}
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                  >
                    {{ $t('government.unfccc_reporting.corresponding_adjustments.table.columns.date') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-light-gray">
                <tr>
                  <td class="px-4 py-3 text-sm">PRJ-2024-001</td>
                  <td class="px-4 py-3 text-sm">Solar Farm Project</td>
                  <td class="px-4 py-3 text-sm">Renewable Energy</td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.unfccc_reporting.corresponding_adjustments.table.adjustment_types.positive') }}</span
                    >
                  </td>
                  <td class="px-4 py-3 text-sm font-medium">125,000</td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >{{ $t('government.unfccc_reporting.corresponding_adjustments.table.status_labels.processed') }}</span
                    >
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600">Dec 10, 2024</td>
                </tr>
                <tr>
                  <td class="px-4 py-3 text-sm">PRJ-2024-002</td>
                  <td class="px-4 py-3 text-sm">Forest Conservation</td>
                  <td class="px-4 py-3 text-sm">Forestry</td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.unfccc_reporting.corresponding_adjustments.table.adjustment_types.negative') }}</span
                    >
                  </td>
                  <td class="px-4 py-3 text-sm font-medium">-45,000</td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                      >{{ $t('government.unfccc_reporting.corresponding_adjustments.table.status_labels.pending') }}</span
                    >
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600">Dec 12, 2024</td>
                </tr>
                <tr>
                  <td class="px-4 py-3 text-sm">PRJ-2024-003</td>
                  <td class="px-4 py-3 text-sm">Wind Energy Project</td>
                  <td class="px-4 py-3 text-sm">Renewable Energy</td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.unfccc_reporting.corresponding_adjustments.table.adjustment_types.positive') }}</span
                    >
                  </td>
                  <td class="px-4 py-3 text-sm font-medium">89,500</td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >{{ $t('government.unfccc_reporting.corresponding_adjustments.table.status_labels.processed') }}</span
                    >
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600">Dec 14, 2024</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Export History -->
        <div
          id="export-history"
          class="bg-white p-5 rounded-md border border-light-gray"
        >
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-medium text-dark-navy">{{ $t('government.unfccc_reporting.export_history.title') }}</h3>
            <button
              class="px-4 py-2 border border-light-gray text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <i class="fa-solid fa-filter mr-2"></i>
              {{ $t('government.unfccc_reporting.export_history.filter') }}
            </button>
          </div>

          <div id="export-list" class="space-y-3">
            <div
              class="flex items-center justify-between p-4 bg-gray-50 rounded-md"
            >
              <div class="flex items-center space-x-4">
                <i class="fa-solid fa-file-pdf text-error-red text-xl"></i>
                <div>
                  <p class="font-medium">UNFCCC_Report_Q4_2024.pdf</p>
                  <p class="text-sm text-gray-600">
                    Generated on Dec 15, 2024 at 10:30 AM
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-3">
                <span
                  class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                  >{{ $t('government.unfccc_reporting.export_history.status.completed') }}</span
                >
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-white"
                >
                  <i class="fa-solid fa-download mr-1"></i>
                  {{ $t('government.unfccc_reporting.export_history.download') }}
                </button>
              </div>
            </div>

            <div
              class="flex items-center justify-between p-4 bg-gray-50 rounded-md"
            >
              <div class="flex items-center space-x-4">
                <i class="fa-solid fa-file-excel text-green-600 text-xl"></i>
                <div>
                  <p class="font-medium">Adjustments_Summary_Q4_2024.xlsx</p>
                  <p class="text-sm text-gray-600">
                    Generated on Dec 14, 2024 at 3:15 PM
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-3">
                <span
                  class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                  >{{ $t('government.unfccc_reporting.export_history.status.completed') }}</span
                >
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-white"
                >
                  <i class="fa-solid fa-download mr-1"></i>
                  {{ $t('government.unfccc_reporting.export_history.download') }}
                </button>
              </div>
            </div>

            <div
              class="flex items-center justify-between p-4 bg-gray-50 rounded-md"
            >
              <div class="flex items-center space-x-4">
                <i class="fa-solid fa-file-csv text-blue-600 text-xl"></i>
                <div></div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
